import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, TextField, useTheme, useMediaQuery, Paper, CircularProgress, Button, Checkbox, ListItemText, OutlinedInput, FormHelperText, Chip, ListSubheader } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBR from 'date-fns/locale/pt-BR';
import SyncIcon from '@mui/icons-material/Sync';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { startOfMonth, endOfMonth, subMonths, startOfWeek, endOfWeek, subWeeks } from 'date-fns';

const FiltersBar = ({
  contrato,
  setContrato,
  centro,
  setCentro,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  equipe,
  setEquipe,
  onCarregar,
  onContratoChange,
  onCentroChange,
  periodoSelecionado: periodoSelecionadoProp,
  onPeriodoChange,
  limitContratoToOne = false,
  availablePeriods = ['ultima-semana', 'ultimo-mes', 'trimestral', 'semestral'], // Períodos disponíveis
  maxDateRange = null // Limite máximo de dias entre startDate e endDate (null = sem limite)
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [contratos, setContratos] = useState(['Todos']);
  const [contratosLoading, setContratosLoading] = useState(false);
  const [contratosError, setContratosError] = useState(null);

  const [centros, setCentros] = useState(['Todos']);
  const [centrosLoading, setCentrosLoading] = useState(false);
  const [centrosError, setCentrosError] = useState(null);

  const [equipes, setEquipes] = useState(['Todos']);
  const [equipesLoading, setEquipesLoading] = useState(false);
  const [equipesError, setEquipesError] = useState(null);

  // Estados para controlar abertura dos menus
  const [openContrato, setOpenContrato] = useState(false);
  const [openCentro, setOpenCentro] = useState(false);
  const [openEquipe, setOpenEquipe] = useState(false);

  // Estados para pesquisa nos filtros
  const [searchContrato, setSearchContrato] = useState('');
  const [searchCentro, setSearchCentro] = useState('');
  const [searchEquipe, setSearchEquipe] = useState('');

  // Estados para controlar quando buscar (só após clicar em Aplicar)
  const [shouldFetchCentros, setShouldFetchCentros] = useState(false);
  const [shouldFetchEquipes, setShouldFetchEquipes] = useState(false);
  const [lastContrato, setLastContrato] = useState([]);
  const [lastCentro, setLastCentro] = useState([]);

  // Estados para controlar quando cada filtro foi confirmado (clicou em Aplicar)
  const [contratoConfirmado, setContratoConfirmado] = useState(false);
  const [centroConfirmado, setCentroConfirmado] = useState(false);
  const [equipeConfirmado, setEquipeConfirmado] = useState(false);

  // Estado para controlar o período selecionado (atalho)
  const [periodoSelecionado, setPeriodoSelecionado] = useState(periodoSelecionadoProp || null);
  
  // Sincronizar com prop externa - aplicar período passado
  useEffect(() => {
    if (periodoSelecionadoProp !== undefined) {
      setPeriodoSelecionado(periodoSelecionadoProp);
      // Aplicar o período automaticamente quando passado
      let datas;
      switch (periodoSelecionadoProp) {
        case 'ultima-semana':
          datas = calcularUltimaSemana();
          break;
        case 'ultimo-mes':
          datas = calcularUltimoMes();
          break;
        case 'trimestral':
          datas = calcularTrimestral();
          break;
        case 'semestral':
          datas = calcularSemestral();
          break;
        default:
          return;
      }
      setStartDate(datas.inicio);
      setEndDate(datas.fim);
    }
  }, [periodoSelecionadoProp]);

  // Flag global para evitar múltiplas requisições em dev/StrictMode
  let contratosCarregados = false;

  // Funções para calcular períodos
  const calcularUltimaSemana = () => {
    const hoje = new Date();
    // Semana anterior (domingo a sábado)
    const semanaAnterior = subWeeks(hoje, 1);
    // Início da semana anterior (domingo)
    const inicio = startOfWeek(semanaAnterior, { weekStartsOn: 0 });
    // Fim da semana anterior (sábado)
    const fim = endOfWeek(semanaAnterior, { weekStartsOn: 0 });
    return { inicio, fim };
  };

  const calcularUltimoMes = () => {
    const hoje = new Date();
    const mesAnterior = subMonths(hoje, 1);
    const inicio = startOfMonth(mesAnterior);
    const fim = endOfMonth(mesAnterior);
    return { inicio, fim };
  };

  const calcularTrimestral = () => {
    const hoje = new Date();
    const mesAnterior = subMonths(hoje, 1);
    const tresMesesAtras = subMonths(mesAnterior, 2);
    const inicio = startOfMonth(tresMesesAtras);
    const fim = endOfMonth(mesAnterior);
    return { inicio, fim };
  };

  const calcularSemestral = () => {
    const hoje = new Date();
    const mesAnterior = subMonths(hoje, 1);
    const seisMesesAtras = subMonths(mesAnterior, 5);
    const inicio = startOfMonth(seisMesesAtras);
    const fim = endOfMonth(mesAnterior);
    return { inicio, fim };
  };

  // Handler para seleção de período
  const handlePeriodoChange = (event, novoPeriodo) => {
    if (novoPeriodo === null) {
      // Se clicar no mesmo botão, deseleciona
      setPeriodoSelecionado(null);
      setStartDate(null);
      setEndDate(null);
      return;
    }

    setPeriodoSelecionado(novoPeriodo);
    let datas;

    switch (novoPeriodo) {
      case 'ultima-semana':
        datas = calcularUltimaSemana();
        break;
      case 'ultimo-mes':
        datas = calcularUltimoMes();
        break;
      case 'trimestral':
        datas = calcularTrimestral();
        break;
      case 'semestral':
        datas = calcularSemestral();
        break;
      default:
        return;
    }

    setStartDate(datas.inicio);
    setEndDate(datas.fim);
    
    // Notificar componente pai sobre mudança de período
    if (onPeriodoChange) {
      onPeriodoChange(novoPeriodo);
    }
  };

  // Handler para mudança de data início com validação de intervalo máximo
  const handleStartDateChange = (newDate) => {
    setStartDate(newDate);
    // Se há um limite de intervalo e endDate está definido, validar
    if (maxDateRange && newDate && endDate) {
      const diffTime = Math.abs(endDate - newDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > maxDateRange) {
        // Ajustar endDate para o limite máximo
        const maxEndDate = new Date(newDate);
        maxEndDate.setDate(maxEndDate.getDate() + maxDateRange);
        setEndDate(maxEndDate);
      }
    }
    // Resetar período selecionado quando datas são alteradas manualmente
    if (periodoSelecionado) {
      setPeriodoSelecionado(null);
      if (onPeriodoChange) {
        onPeriodoChange(null);
      }
    }
  };

  // Handler para mudança de data fim com validação de intervalo máximo
  const handleEndDateChange = (newDate) => {
    if (!newDate) {
      setEndDate(null);
      return;
    }
    
    if (maxDateRange && startDate) {
      const diffTime = Math.abs(newDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > maxDateRange) {
        // Não permitir se ultrapassar o limite - ajustar para o máximo permitido
        const maxEndDate = new Date(startDate);
        maxEndDate.setDate(maxEndDate.getDate() + maxDateRange);
        setEndDate(maxEndDate);
        return;
      }
    }
    
    setEndDate(newDate);
    // Resetar período selecionado quando datas são alteradas manualmente
    if (periodoSelecionado) {
      setPeriodoSelecionado(null);
      if (onPeriodoChange) {
        onPeriodoChange(null);
      }
    }
  };

  // Resetar período quando datas forem alteradas manualmente (apenas se não for o período selecionado)
  // Nota: Este useEffect é desabilitado porque pode causar loops. O período é resetado apenas quando o usuário clica em outro botão.

  // Função utilitária para verificar se contrato está vazio (considera limitContratoToOne)
  const isContratoEmpty = () => {
    if (limitContratoToOne) {
      return Array.isArray(contrato) ? contrato.length === 0 : !contrato;
    }
    return Array.isArray(contrato) ? contrato.length === 0 : false;
  };

  // Função utilitária para lidar com seleção múltipla e 'Todos'
  const isAllSelected = (selected, options) => selected.length === options.filter(opt => opt !== 'Todos').length;
  const handleMultiSelect = (value, options, prevSelected) => {
    const realOptions = options.filter(opt => opt !== 'Todos');
    if (value.includes('Todos')) {
      // Toggle: se já estava tudo selecionado, desmarca tudo
      if (prevSelected && isAllSelected(prevSelected, options)) {
        return [];
      }
      // Selecionou 'Todos': marca todas as opções reais
      return realOptions;
    }
    if (value.length === 0) {
      // Nada selecionado: filtro vazio
      return [];
    }
    // Nunca inclua 'Todos' no array de seleção
    return value.filter(opt => opt !== 'Todos');
  };

  // Buscar contratos ao montar
  useEffect(() => {
    if (contratosCarregados) return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    contratosCarregados = true;
    const fetchContratos = async () => {
      setContratosLoading(true);
      setContratosError(null);
      try {
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const resp = await fetch(`${apiUrl}/contratos`);
        const data = await resp.json();
        const contratosList = ['Todos', ...data.map(c => c.des_contrato || c.nome || c)];
        setContratos(contratosList);
        // Selecionar todos os contratos por padrão para melhor UX
        if (contrato.length === 0) {
          setContrato(contratosList.filter(c => c !== 'Todos'));
          setContratoConfirmado(true);
          setShouldFetchCentros(true);
        }
      } catch (err) {
        setContratosError('Erro ao buscar contratos');
      } finally {
        setContratosLoading(false);
      }
    };
    fetchContratos();
  }, []);

  // Buscar centros APENAS quando fechar o menu de contrato (shouldFetchCentros = true)
  useEffect(() => {
    if (!shouldFetchCentros) return;
    setShouldFetchCentros(false);

    // Verificar se contrato mudou desde a última busca
    const contratoAtual = Array.isArray(contrato) ? contrato : [];
    const contratoAnterior = Array.isArray(lastContrato) ? lastContrato : [];
    const mudou = JSON.stringify(contratoAtual.sort()) !== JSON.stringify(contratoAnterior.sort());
    
    if (!mudou && contratoAtual.length > 0) return; // Não mudou, não precisa buscar
    
    setLastContrato(contratoAtual);

    if (!contrato || (Array.isArray(contrato) && contrato.length === 0)) {
      setCentros(['Todos']);
      setCentro([]); // Limpa seleção
      setEquipes(['Todos']);
      setEquipe([]);
      return;
    }
    const fetchCentros = async () => {
      setCentrosLoading(true);
      setCentrosError(null);
      try {
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const contratosSelecionados = Array.isArray(contrato) ? contrato : [contrato];
        
        // Usar o novo endpoint /listar-centros-servico com filtro como query parameter
        const resp = await fetch(`${apiUrl}/listar-centros-servico?des_contrato=${encodeURIComponent(contratosSelecionados.join(','))}`);
        const data = await resp.json();
        
        // Filtrar centros no frontend enquanto o backend não aplica o filtro corretamente
        const centrosFiltrados = data.filter(c => {
          const contratoCentro = c.num_cont_rcb;
          return contratosSelecionados.some(contrato => contratoCentro === contrato);
        });
        
        const centrosList = ['Todos', ...centrosFiltrados.map(c => c.centro_servicos_nome || c.des_centro_servico || c.nome || c)];
        setCentros(centrosList);
        // Auto-selecionar todos os centros quando cascading do auto-fill
        if (centro.length === 0) {
          setCentro(centrosList.filter(c => c !== 'Todos'));
          setCentroConfirmado(true);
          setShouldFetchEquipes(true);
        }
        setEquipes(['Todos']);
        setEquipe([]);
      } catch (err) {
        setCentrosError('Erro ao buscar centros');
      } finally {
        setCentrosLoading(false);
      }
    };
    fetchCentros();
    if (onContratoChange) onContratoChange(contrato);
  }, [shouldFetchCentros, contrato, lastContrato, onContratoChange, setCentro, setEquipe, centro.length]);

  // Buscar equipes APENAS quando fechar o menu de centro (shouldFetchEquipes = true)
  useEffect(() => {
    if (!shouldFetchEquipes) return;
    setShouldFetchEquipes(false);

    // Verificar se centro mudou desde a última busca
    const centroAtual = Array.isArray(centro) ? centro : [];
    const centroAnterior = Array.isArray(lastCentro) ? lastCentro : [];
    const mudou = JSON.stringify(centroAtual.sort()) !== JSON.stringify(centroAnterior.sort());
    
    if (!mudou && centroAtual.length > 0) return; // Não mudou, não precisa buscar
    
    setLastCentro(centroAtual);

    if (!centro || (Array.isArray(centro) && centro.length === 0)) {
      setEquipes(['Todos']);
      setEquipe([]);
      return;
    }
    const fetchEquipes = async () => {
      setEquipesLoading(true);
      setEquipesError(null);
      try {
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const centrosSelecionados = Array.isArray(centro) ? centro : [centro];
        const contratosSelecionados = Array.isArray(contrato) ? contrato : (contrato ? [contrato] : []);
        const resp = await fetch(`${apiUrl}/equipes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            des_centro_servico: centrosSelecionados,
            des_contrato: contratosSelecionados,
          }),
        });
        const data = await resp.json();
        const equipesList = ['Todos', ...data.map(e => e.des_equipe_cms || e.nome || e)];
        setEquipes(equipesList);
        // Auto-selecionar todas as equipes quando cascading do auto-fill
        if (equipe.length === 0) {
          setEquipe(equipesList.filter(e => e !== 'Todos'));
          setEquipeConfirmado(true);
        }
      } catch (err) {
        setEquipesError('Erro ao buscar equipes');
      } finally {
        setEquipesLoading(false);
      }
    };
    fetchEquipes();
    if (onCentroChange) onCentroChange(centro);
  }, [shouldFetchEquipes, centro, lastCentro, onCentroChange, setEquipe, equipe.length]);

  // Ao montar, defina a data inicial e final como ontem
  // Datas começam vazias - usuário deve preencher manualmente

  // Filtrar opções baseado na pesquisa
  const filteredContratos = contratos.filter(c => 
    c === 'Todos' || c.toLowerCase().includes(searchContrato.toLowerCase())
  );
  const filteredCentros = centros.filter(c => 
    c === 'Todos' || c.toLowerCase().includes(searchCentro.toLowerCase())
  );
  const filteredEquipes = equipes.filter(e => 
    e === 'Todos' || e.toLowerCase().includes(searchEquipe.toLowerCase())
  );

  // Estilos para o campo de busca
  const searchFieldStyles = {
    container: { 
      p: 1.5, 
      position: 'sticky', 
      top: 0, 
      bgcolor: '#f5f5f5', 
      zIndex: 10, 
      borderBottom: '1px solid #ddd',
      lineHeight: 'normal'
    },
    inputWrapper: { 
      position: 'relative', 
      display: 'flex', 
      alignItems: 'center' 
    },
    icon: { 
      position: 'absolute', 
      left: 10, 
      color: '#666',
      fontSize: 20,
      pointerEvents: 'none'
    },
    input: {
      width: '100%',
      padding: '10px 12px 10px 38px',
      fontSize: '14px',
      border: '1px solid #ccc',
      borderRadius: '6px',
      outline: 'none',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      color: '#333',
      caretColor: '#333',
    }
  };

  // Componente de rodapé do menu com botões de ação
  const MenuFooter = ({ selectedCount, onClear, onApply, total }) => (
    <ListSubheader 
      sx={{ 
        p: 1, 
        position: 'sticky', 
        bottom: 0, 
        bgcolor: '#f5f5f5', 
        zIndex: 10, 
        borderTop: '1px solid #ddd',
        lineHeight: 'normal',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip 
          label={`${selectedCount} de ${total} selecionado${selectedCount !== 1 ? 's' : ''}`}
          size="small"
          color={selectedCount > 0 ? 'primary' : 'secondary'}
          variant={selectedCount > 0 ? 'filled' : 'outlined'}
          sx={selectedCount === 0 ? { 
            borderColor: '#FF9600', 
            color: '#FF9600',
            '& .MuiChip-label': { color: '#FF9600' }
          } : {}}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<CloseIcon fontSize="small" />}
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          disabled={selectedCount === 0}
          sx={{ 
            textTransform: 'none',
            fontSize: '12px',
            py: 0.5,
            minWidth: 'auto'
          }}
        >
          Limpar
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<CheckIcon fontSize="small" />}
          onClick={(e) => {
            e.stopPropagation();
            onApply();
          }}
          sx={{ 
            textTransform: 'none',
            fontSize: '12px',
            py: 0.5,
            minWidth: 'auto'
          }}
        >
          Aplicar
        </Button>
      </Box>
    </ListSubheader>
  );

  // MenuProps base para os Selects - SEM scroll automático para o topo
  const getMenuProps = () => ({
    PaperProps: {
      style: {
        maxHeight: 400,
      },
    },
    // Manter o menu aberto enquanto digita e não mover o scroll
    autoFocus: false,
    disableAutoFocusItem: true,
  });

  // Determinar qual alerta mostrar

  return (
    <Paper
      elevation={3}
      sx={{
        mb: 4,
        p: isMobile ? 2 : 3,
        borderRadius: 3,
        boxShadow: theme.shadows[3],
        background: theme.palette.background.paper,
      }}
    >

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: { xs: 1.5, sm: 2, md: 2.5 },
        flexWrap: 'wrap',
        '& > *': {
          alignSelf: 'flex-start'
        }
      }}>
          <FormControl 
            sx={{ 
              minWidth: 160, 
              maxWidth: 200, 
              flex: '1 1 180px',
              display: 'flex',
              flexDirection: 'column',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
                },
                '50%': {
                  boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                },
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: isContratoEmpty() ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                transition: 'all 0.3s ease-in-out',
                ...(isContratoEmpty() && {
                  animation: 'pulse 1.5s infinite',
                  borderColor: '#1976d2',
                  borderWidth: '2px',
                  '& fieldset': {
                    borderColor: '#1976d2 !important',
                    borderWidth: '2px',
                  },
                }),
                '&:hover': {
                  backgroundColor: isContratoEmpty() ? 'rgba(25, 118, 210, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                  transform: 'none',
                }
              }
            }} 
            disabled={contratosLoading} 
            error={!!contratosError}
            required
          >
            <InputLabel 
              id="contrato-label"
              sx={{
                fontWeight: isContratoEmpty() ? 700 : 400,
                color: isContratoEmpty() ? '#1976d2' : 'inherit',
                fontSize: isContratoEmpty() ? '1.1rem' : '1rem',
                textShadow: isContratoEmpty() ? '0 0 10px rgba(25, 118, 210, 0.3)' : 'none',
              }}
            >
              {isContratoEmpty() ? '① CONTRATO(S) *' : 'Contrato'}
            </InputLabel>
            <Select
              multiple={!limitContratoToOne}
              labelId="contrato-label"
              value={limitContratoToOne 
                ? (Array.isArray(contrato) && contrato.length > 0 
                    ? (contrato[0] || '') 
                    : (typeof contrato === 'string' && contrato !== 'Todos' 
                        ? contrato 
                        : ''))
                : (Array.isArray(contrato) ? contrato : contrato === 'Todos' ? [] : [contrato])
              }
              label={limitContratoToOne 
                ? ((Array.isArray(contrato) ? contrato.length === 0 : !contrato) ? '① Selecione Contrato *' : 'Contrato')
                : (contrato.length === 0 ? '① Selecione Contrato(s) *' : 'Contrato')
              }
              open={openContrato}
              onOpen={() => setOpenContrato(true)}
              onClose={() => {
                setOpenContrato(false);
                setSearchContrato(''); // Limpa a busca ao fechar
                if (!limitContratoToOne) {
                  // Para múltipla seleção, dispara busca de centros ao fechar o menu
                  setShouldFetchCentros(true);
                }
                // Para single select (limitContratoToOne), a busca já foi disparada no onChange
              }}
              onChange={e => {
                if (limitContratoToOne) {
                  // Limitar a apenas 1 contrato
                  const selectedValue = e.target.value;
                  if (selectedValue === 'Todos' || selectedValue.length === 0) {
                    setContrato([]);
                    setContratoConfirmado(false);
                    // Limpar campos dependentes
                    setCentro([]);
                    setEquipe([]);
                    setStartDate(null);
                    setEndDate(null);
                    setPeriodoSelecionado(null);
                    if (onPeriodoChange) {
                      onPeriodoChange(null);
                    }
                  } else {
                    // Se for array, pega o primeiro; se for string, usa direto
                    const singleValue = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;
                    const contratoAnterior = Array.isArray(contrato) && contrato.length > 0 ? contrato[0] : contrato;
                    
                    // Verificar se o contrato mudou
                    const contratoMudou = singleValue !== contratoAnterior;
                    
                    setContrato([singleValue]);
                    
                    // Se o contrato mudou, limpar campos dependentes
                    if (contratoMudou) {
                      setCentro([]);
                      setEquipe([]);
                      setStartDate(null);
                      setEndDate(null);
                      setCentroConfirmado(false);
                      setEquipeConfirmado(false);
                      setPeriodoSelecionado(null);
                      if (onPeriodoChange) {
                        onPeriodoChange(null);
                      }
                    }
                    
                    // Para single select, confirma e dispara busca de centros imediatamente
                    setContratoConfirmado(true);
                    setShouldFetchCentros(true);
                  }
                } else {
                  const val = handleMultiSelect(e.target.value, contratos, contrato);
                  const contratoMudou = JSON.stringify(val.sort()) !== JSON.stringify((Array.isArray(contrato) ? contrato : []).sort());
                  
                  setContrato(val);
                  
                  // Se o contrato mudou, limpar campos dependentes
                  if (contratoMudou) {
                    setCentro([]);
                    setEquipe([]);
                    setStartDate(null);
                    setEndDate(null);
                    setCentroConfirmado(false);
                    setEquipeConfirmado(false);
                    setPeriodoSelecionado(null);
                    if (onPeriodoChange) {
                      onPeriodoChange(null);
                    }
                  }
                }
                // Não fecha mais automaticamente - permite múltiplas seleções
                // A lista fecha quando o usuário clica fora (onClose)
              }}
              input={<OutlinedInput label={limitContratoToOne 
                ? (isContratoEmpty() ? '① Selecione Contrato *' : 'Contrato')
                : (isContratoEmpty() ? '① Selecione Contrato(s) *' : 'Contrato')
              } />}
              renderValue={selected => {
                if (limitContratoToOne) {
                  // Single select
                  if (!selected || selected === '' || selected === 'Todos') {
                    return <Box sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      Nenhum selecionado
                    </Box>;
                  }
                  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    {selected}
                  </Box>;
                } else {
                  // Multiple select
                  const selectedArray = Array.isArray(selected) ? selected : [];
                  if (selectedArray.length === 0) {
                    return <Box sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      Nenhum selecionado
                    </Box>;
                  }
                  return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    {selectedArray.length === 1 ? selectedArray[0] : `${selectedArray.length} selecionados`}
                  </Box>;
                }
              }}
              startAdornment={contratosLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              MenuProps={getMenuProps()}
            >
              <ListSubheader sx={searchFieldStyles.container}>
                <Box sx={searchFieldStyles.inputWrapper}>
                  <SearchIcon sx={searchFieldStyles.icon} />
                  <input
                    type="text"
                    placeholder="Pesquisar contrato..."
                    value={searchContrato}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSearchContrato(e.target.value);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    style={searchFieldStyles.input}
                  />
                </Box>
              </ListSubheader>
              {!limitContratoToOne && !searchContrato && (
                <MenuItem value="Todos">
                  <Checkbox
                    checked={isAllSelected(Array.isArray(contrato) ? contrato : [], contratos)}
                    indeterminate={contrato.length > 0 && !isAllSelected(contrato, contratos)}
                  />
                  <ListItemText primary="Todos" />
                </MenuItem>
              )}
              {filteredContratos.filter(c => c !== 'Todos').map((c) => (
                <MenuItem key={c} value={c}>
                  {!limitContratoToOne && (
                    <Checkbox checked={Array.isArray(contrato) ? contrato.indexOf(c) > -1 : contrato === c} />
                  )}
                  <ListItemText primary={c} />
                </MenuItem>
              ))}
              {filteredContratos.filter(c => c !== 'Todos').length === 0 && searchContrato && (
                <MenuItem disabled>
                  <ListItemText primary="Nenhum contrato encontrado" sx={{ fontStyle: 'italic', color: 'text.secondary' }} />
                </MenuItem>
              )}
              {!limitContratoToOne && (
                <MenuFooter 
                  selectedCount={Array.isArray(contrato) ? contrato.length : (contrato ? 1 : 0)}
                  total={contratos.filter(c => c !== 'Todos').length}
                  onClear={() => {
                    setContrato([]);
                    setContratoConfirmado(false);
                  }}
                  onApply={() => {
                    setOpenContrato(false);
                    setSearchContrato('');
                    const hasContrato = limitContratoToOne 
                      ? (Array.isArray(contrato) ? contrato.length > 0 : !!contrato)
                      : (Array.isArray(contrato) ? contrato.length > 0 : !!contrato);
                    if (hasContrato) {
                      setContratoConfirmado(true);
                      setShouldFetchCentros(true);
                    }
                  }}
                />
              )}
            </Select>
            <Box sx={{ minHeight: '20px', mt: 0.5 }}>
              {limitContratoToOne ? (
                <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', fontWeight: 500, m: 0, mt: 0.5 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  Apenas 1 contrato por consulta
                </FormHelperText>
              ) : (
                <>
                  {contrato.length === 0 && !contratosLoading && (
                    <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', fontWeight: 500, m: 0, mt: 0.5 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                      Selecione pelo menos um contrato para continuar
                    </FormHelperText>
                  )}
                  {contrato.length > 0 && (
                    <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', m: 0, mt: 0.5 }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                      {contrato.length} contrato{contrato.length > 1 ? 's' : ''} selecionado{contrato.length > 1 ? 's' : ''}
                    </FormHelperText>
                  )}
                </>
              )}
            </Box>
          </FormControl>
          <FormControl 
            sx={{ 
              minWidth: 140, 
              maxWidth: 180, 
              flex: '1 1 150px',
              display: 'flex',
              flexDirection: 'column',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
                },
                '50%': {
                  boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                },
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: contratoConfirmado && centro.length === 0 ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                transition: 'all 0.3s ease-in-out',
                ...(contratoConfirmado && centro.length === 0 && {
                  animation: 'pulse 1.5s infinite',
                  borderColor: '#1976d2',
                  borderWidth: '2px',
                  '& fieldset': {
                    borderColor: '#1976d2 !important',
                    borderWidth: '2px',
                  },
                }),
                '&:hover': {
                  backgroundColor: contratoConfirmado && centro.length === 0 ? 'rgba(25, 118, 210, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                  transform: 'none',
                }
              }
            }} 
            disabled={centrosLoading || !contratoConfirmado} 
            error={!!centrosError}
          >
            <InputLabel 
              id="centro-label"
              sx={{
                fontWeight: contratoConfirmado && centro.length === 0 ? 700 : 400,
                color: contratoConfirmado && centro.length === 0 ? '#FF9600' : 'inherit',
                fontSize: contratoConfirmado && centro.length === 0 ? '1.1rem' : '1rem',
                textShadow: contratoConfirmado && centro.length === 0 ? '0 0 10px rgba(255, 150, 0, 0.3)' : 'none',
              }}
            >
              {!contratoConfirmado ? 'Centro de Serviço' : (centro.length === 0 ? '② CENTRO(S) *' : 'Centro de Serviço')}
            </InputLabel>
            <Select
              multiple
              labelId="centro-label"
              value={Array.isArray(centro) ? centro : centro === 'Todos' ? [] : [centro]}
              label={isContratoEmpty() ? 'Centro de Serviço' : (centro.length === 0 ? '② Selecione Centro(s)' : 'Centro de Serviço')}
              open={openCentro}
              onOpen={() => setOpenCentro(true)}
              onClose={() => {
                setOpenCentro(false);
                setSearchCentro(''); // Limpa a busca ao fechar
                setShouldFetchEquipes(true); // Dispara busca de equipes ao fechar o menu
              }}
              onChange={e => {
                const val = handleMultiSelect(e.target.value, centros, centro);
                setCentro(val);
                // Não fecha mais automaticamente - permite múltiplas seleções
                // A lista fecha quando o usuário clica fora (onClose)
              }}
              input={<OutlinedInput label={isContratoEmpty() ? 'Centro de Serviço' : (centro.length === 0 ? '② Selecione Centro(s)' : 'Centro de Serviço')} />}
              renderValue={selected => {
                if (isContratoEmpty()) {
                  return <Box sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    Selecione contrato primeiro
                  </Box>;
                }
                if (selected.length === 0) {
                  return <Box sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    Nenhum selecionado
                  </Box>;
                }
                return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  {selected.length === 1 ? selected[0] : `${selected.length} selecionados`}
                </Box>;
              }}
              startAdornment={centrosLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              MenuProps={getMenuProps()}
            >
              <ListSubheader sx={searchFieldStyles.container}>
                <Box sx={searchFieldStyles.inputWrapper}>
                  <SearchIcon sx={searchFieldStyles.icon} />
                  <input
                    type="text"
                    placeholder="Pesquisar centro..."
                    value={searchCentro}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSearchCentro(e.target.value);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    style={searchFieldStyles.input}
                  />
                </Box>
              </ListSubheader>
              {!searchCentro && (
                <MenuItem value="Todos">
                  <Checkbox
                    checked={isAllSelected(Array.isArray(centro) ? centro : [], centros)}
                    indeterminate={centro.length > 0 && !isAllSelected(centro, centros)}
                  />
                  <ListItemText primary="Todos" />
                </MenuItem>
              )}
              {filteredCentros.filter(c => c !== 'Todos').map((c) => (
                <MenuItem key={c} value={c}>
                  <Checkbox checked={Array.isArray(centro) ? centro.indexOf(c) > -1 : centro === c} />
                  <ListItemText primary={c} />
                </MenuItem>
              ))}
              {filteredCentros.filter(c => c !== 'Todos').length === 0 && searchCentro && (
                <MenuItem disabled>
                  <ListItemText primary="Nenhum centro encontrado" sx={{ fontStyle: 'italic', color: 'text.secondary' }} />
                </MenuItem>
              )}
              <MenuFooter 
                selectedCount={centro.length}
                total={centros.filter(c => c !== 'Todos').length}
                onClear={() => {
                  setCentro([]);
                  setCentroConfirmado(false);
                }}
                onApply={() => {
                  setOpenCentro(false);
                  setSearchCentro('');
                  if (centro.length > 0) {
                    setCentroConfirmado(true);
                    setShouldFetchEquipes(true);
                  }
                }}
              />
            </Select>
            <Box sx={{ minHeight: '20px', mt: 0.5 }}>
              {!contratoConfirmado && (
                <FormHelperText sx={{ color: 'text.secondary', m: 0, mt: 0.5 }}>
                  Aguardando confirmação de contrato
                </FormHelperText>
              )}
              {contratoConfirmado && centro.length === 0 && !centrosLoading && (
                <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', fontWeight: 500, m: 0, mt: 0.5 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  Selecione pelo menos um centro
                </FormHelperText>
              )}
              {centro.length > 0 && (
                <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', m: 0, mt: 0.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                  {centro.length} centro{centro.length > 1 ? 's' : ''} selecionado{centro.length > 1 ? 's' : ''}
                </FormHelperText>
              )}
            </Box>
          </FormControl>
          <FormControl 
            sx={{ 
              minWidth: 140, 
              maxWidth: 180, 
              flex: '1 1 150px',
              display: 'flex',
              flexDirection: 'column',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
                },
                '50%': {
                  boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                },
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: centroConfirmado && equipe.length === 0 ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                transition: 'all 0.3s ease-in-out',
                ...(centroConfirmado && equipe.length === 0 && {
                  animation: 'pulse 1.5s infinite',
                  borderColor: '#1976d2',
                  borderWidth: '2px',
                  '& fieldset': {
                    borderColor: '#1976d2 !important',
                    borderWidth: '2px',
                  },
                }),
                '&:hover': {
                  backgroundColor: centroConfirmado && equipe.length === 0 ? 'rgba(25, 118, 210, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                  transform: 'none',
                }
              }
            }} 
            disabled={equipesLoading || !centroConfirmado} 
            error={!!equipesError}
          >
            <InputLabel 
              id="equipe-label"
              sx={{
                fontWeight: centroConfirmado && equipe.length === 0 ? 700 : 400,
                color: centroConfirmado && equipe.length === 0 ? '#FF9600' : 'inherit',
                fontSize: centroConfirmado && equipe.length === 0 ? '1.1rem' : '1rem',
                textShadow: centroConfirmado && equipe.length === 0 ? '0 0 10px rgba(255, 150, 0, 0.3)' : 'none',
              }}
            >
              {!centroConfirmado ? 'Equipe' : (equipe.length === 0 ? '③ EQUIPE(S) *' : 'Equipe')}
            </InputLabel>
            <Select
              multiple
              labelId="equipe-label"
              value={Array.isArray(equipe) ? equipe : equipe === 'Todos' ? [] : [equipe]}
              label={centro.length === 0 ? 'Equipe' : (equipe.length === 0 ? '③ Selecione Equipe(s)' : 'Equipe')}
              open={openEquipe}
              onOpen={() => setOpenEquipe(true)}
              onClose={() => {
                setOpenEquipe(false);
                setSearchEquipe(''); // Limpa a busca ao fechar
              }}
              onChange={e => {
                const val = handleMultiSelect(e.target.value, equipes, equipe);
                setEquipe(val);
                // Não fecha mais automaticamente - permite múltiplas seleções
                // A lista fecha quando o usuário clica fora (onClose)
              }}
              input={<OutlinedInput label={centro.length === 0 ? 'Equipe' : (equipe.length === 0 ? '③ Selecione Equipe(s)' : 'Equipe')} />}
              renderValue={selected => {
                if (centro.length === 0) {
                  return <Box sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    Selecione centro primeiro
                  </Box>;
                }
                if (selected.length === 0) {
                  return <Box sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    Nenhum selecionado
                  </Box>;
                }
                return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  {selected.length === 1 ? selected[0] : `${selected.length} selecionados`}
                </Box>;
              }}
              startAdornment={equipesLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              MenuProps={getMenuProps()}
            >
              <ListSubheader sx={searchFieldStyles.container}>
                <Box sx={searchFieldStyles.inputWrapper}>
                  <SearchIcon sx={searchFieldStyles.icon} />
                  <input
                    type="text"
                    placeholder="Pesquisar equipe..."
                    value={searchEquipe}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSearchEquipe(e.target.value);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    style={searchFieldStyles.input}
                  />
                </Box>
              </ListSubheader>
              {!searchEquipe && (
                <MenuItem value="Todos">
                  <Checkbox
                    checked={isAllSelected(Array.isArray(equipe) ? equipe : [], equipes)}
                    indeterminate={equipe.length > 0 && !isAllSelected(equipe, equipes)}
                  />
                  <ListItemText primary="Todos" />
                </MenuItem>
              )}
              {filteredEquipes.filter(e => e !== 'Todos').map((e) => (
                <MenuItem key={e} value={e}>
                  <Checkbox checked={Array.isArray(equipe) ? equipe.indexOf(e) > -1 : equipe === e} />
                  <ListItemText primary={e} />
                </MenuItem>
              ))}
              {filteredEquipes.filter(e => e !== 'Todos').length === 0 && searchEquipe && (
                <MenuItem disabled>
                  <ListItemText primary="Nenhuma equipe encontrada" sx={{ fontStyle: 'italic', color: 'text.secondary' }} />
                </MenuItem>
              )}
              <MenuFooter 
                selectedCount={equipe.length}
                total={equipes.filter(e => e !== 'Todos').length}
                onClear={() => {
                  setEquipe([]);
                  setEquipeConfirmado(false);
                }}
                onApply={() => {
                  setOpenEquipe(false);
                  setSearchEquipe('');
                  if (equipe.length > 0) {
                    setEquipeConfirmado(true);
                  }
                }}
              />
            </Select>
            <Box sx={{ minHeight: '20px', mt: 0.5 }}>
              {!centroConfirmado && (
                <FormHelperText sx={{ color: 'text.secondary', m: 0, mt: 0.5 }}>
                  Aguardando confirmação de centro
                </FormHelperText>
              )}
              {centroConfirmado && equipe.length === 0 && !equipesLoading && (
                <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', fontWeight: 500, m: 0, mt: 0.5 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                  Selecione pelo menos uma equipe
                </FormHelperText>
              )}
              {equipe.length > 0 && (
                <FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main', m: 0, mt: 0.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                  {equipe.length} equipe{equipe.length > 1 ? 's' : ''} selecionada{equipe.length > 1 ? 's' : ''}
                </FormHelperText>
              )}
            </Box>
          </FormControl>
          {/* Botões de atalho de período */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              minWidth: { xs: 240, sm: 300, md: 360 },
              maxWidth: { xs: 280, sm: 340, md: 400 },
              flex: '1 1 200px',
              alignSelf: 'flex-start',
              position: 'relative',
              mr: { xs: 2, sm: 2.5, md: 3 }
            }}
          >
            <Box sx={{ 
              height: '56px', 
              display: 'flex', 
              alignItems: 'center',
              gap: 1
            }}>
              {availablePeriods.includes('ultima-semana') && (
                <Button
                  variant={periodoSelecionado === 'ultima-semana' ? 'contained' : 'outlined'}
                  onClick={() => handlePeriodoChange(null, periodoSelecionado === 'ultima-semana' ? null : 'ultima-semana')}
                  size="small"
                  sx={{
                    flex: 1,
                    height: '56px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    borderColor: !periodoSelecionado ? '#FF9600' : 'grey.300',
                    color: !periodoSelecionado ? '#FFFFFF' : (periodoSelecionado === 'ultima-semana' ? 'white' : 'text.primary'),
                    backgroundColor: !periodoSelecionado ? '#FF9600' : (periodoSelecionado === 'ultima-semana' ? 'primary.main' : 'white'),
                    '&:hover': {
                      backgroundColor: !periodoSelecionado ? '#CC7900' : (periodoSelecionado === 'ultima-semana' ? 'primary.dark' : 'primary.light'),
                      color: 'white',
                      borderColor: !periodoSelecionado ? '#CC7900' : (periodoSelecionado === 'ultima-semana' ? 'primary.dark' : 'primary.light'),
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Última Semana
                </Button>
              )}
              {availablePeriods.includes('ultimo-mes') && (
                <Button
                  variant={periodoSelecionado === 'ultimo-mes' ? 'contained' : 'outlined'}
                  onClick={() => handlePeriodoChange(null, periodoSelecionado === 'ultimo-mes' ? null : 'ultimo-mes')}
                  size="small"
                  sx={{
                    flex: 1,
                    height: '56px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    borderColor: !periodoSelecionado ? '#FF9600' : 'grey.300',
                    color: !periodoSelecionado ? '#FFFFFF' : (periodoSelecionado === 'ultimo-mes' ? 'white' : 'text.primary'),
                    backgroundColor: !periodoSelecionado ? '#FF9600' : (periodoSelecionado === 'ultimo-mes' ? 'primary.main' : 'white'),
                    '&:hover': {
                      backgroundColor: !periodoSelecionado ? '#CC7900' : (periodoSelecionado === 'ultimo-mes' ? 'primary.dark' : 'primary.light'),
                      color: 'white',
                      borderColor: !periodoSelecionado ? '#CC7900' : (periodoSelecionado === 'ultimo-mes' ? 'primary.dark' : 'primary.light'),
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Último Mês
                </Button>
              )}
              {availablePeriods.includes('trimestral') && (
                <Button
                  variant={periodoSelecionado === 'trimestral' ? 'contained' : 'outlined'}
                  onClick={() => handlePeriodoChange(null, periodoSelecionado === 'trimestral' ? null : 'trimestral')}
                  size="small"
                  sx={{
                    flex: 1,
                    height: '56px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    borderColor: !periodoSelecionado ? '#FF9600' : 'grey.300',
                    color: !periodoSelecionado ? '#FFFFFF' : (periodoSelecionado === 'trimestral' ? 'white' : 'text.primary'),
                    backgroundColor: !periodoSelecionado ? '#FF9600' : (periodoSelecionado === 'trimestral' ? 'primary.main' : 'white'),
                    '&:hover': {
                      backgroundColor: !periodoSelecionado ? '#CC7900' : (periodoSelecionado === 'trimestral' ? 'primary.dark' : 'primary.light'),
                      color: 'white',
                      borderColor: !periodoSelecionado ? '#CC7900' : (periodoSelecionado === 'trimestral' ? 'primary.dark' : 'primary.light'),
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Trimestral
                </Button>
              )}
              {availablePeriods.includes('semestral') && (
                <Button
                  variant={periodoSelecionado === 'semestral' ? 'contained' : 'outlined'}
                  onClick={() => handlePeriodoChange(null, periodoSelecionado === 'semestral' ? null : 'semestral')}
                  size="small"
                  sx={{
                    flex: 1,
                    height: '56px',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    borderColor: !periodoSelecionado ? '#FF9600' : 'grey.300',
                    color: !periodoSelecionado ? '#FFFFFF' : (periodoSelecionado === 'semestral' ? 'white' : 'text.primary'),
                    backgroundColor: !periodoSelecionado ? '#FF9600' : (periodoSelecionado === 'semestral' ? 'primary.main' : 'white'),
                    '&:hover': {
                      backgroundColor: !periodoSelecionado ? '#CC7900' : (periodoSelecionado === 'semestral' ? 'primary.dark' : 'primary.light'),
                      color: 'white',
                      borderColor: !periodoSelecionado ? '#CC7900' : (periodoSelecionado === 'semestral' ? 'primary.dark' : 'primary.light'),
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Semestral
                </Button>
              )}
            </Box>
            <Box sx={{ minHeight: '20px', mt: 0.5 }}>
              <FormHelperText sx={{ 
                color: 'text.secondary', 
                m: 0, 
                mt: 0.5,
                fontSize: '0.75rem'
              }}>
              
              </FormHelperText>
            </Box>
          </Box>
          <Box 
            sx={{ 
              minWidth: { xs: 130, sm: 150, md: 160 },
              maxWidth: { xs: 150, sm: 170, md: 180 },
              flex: '1 1 150px',
              opacity: periodoSelecionado ? 0.6 : 1,
              transition: 'opacity 0.3s ease',
              alignSelf: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
                },
                '50%': {
                  boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                },
              },
              ...(!startDate && equipeConfirmado && !periodoSelecionado && {
                animation: 'pulse 1.5s infinite',
                borderRadius: 1,
              }),
              '& .MuiInputLabel-root': {
                fontWeight: !startDate && equipeConfirmado && !periodoSelecionado ? 700 : 400,
                color: !startDate && equipeConfirmado && !periodoSelecionado ? '#1976d2' : 'inherit',
                fontSize: !startDate && equipeConfirmado && !periodoSelecionado ? '1.1rem' : '1rem',
                textShadow: !startDate && equipeConfirmado && !periodoSelecionado ? '0 0 10px rgba(25, 118, 210, 0.3)' : 'none',
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: !startDate && equipeConfirmado && !periodoSelecionado ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                transition: 'all 0.3s ease-in-out',
                height: '56px',
                ...(!startDate && equipeConfirmado && !periodoSelecionado && {
                  '& fieldset': {
                    borderColor: '#1976d2 !important',
                    borderWidth: '2px',
                  },
                }),
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label={!startDate && equipeConfirmado && !periodoSelecionado ? "DATA INÍCIO *" : "Data Início"}
                value={startDate}
                onChange={handleStartDateChange}
                disabled={!!periodoSelecionado}
                maxDate={
                  maxDateRange && endDate 
                    ? (() => {
                        const maxDate = new Date(endDate);
                        return maxDate;
                      })()
                    : null
                }
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    disabled={!!periodoSelecionado}
                  />
                )}
              />
            </LocalizationProvider>
            <Box sx={{ minHeight: '20px', mt: 0.5 }} />
          </Box>
          <Box 
            sx={{ 
              minWidth: { xs: 130, sm: 150, md: 160 },
              maxWidth: { xs: 150, sm: 170, md: 180 },
              flex: '1 1 150px',
              opacity: periodoSelecionado ? 0.6 : 1,
              transition: 'opacity 0.3s ease',
              alignSelf: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
                },
                '50%': {
                  boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                },
              },
              ...(!endDate && equipeConfirmado && !periodoSelecionado && {
                animation: 'pulse 1.5s infinite',
                borderRadius: 1,
              }),
              '& .MuiInputLabel-root': {
                fontWeight: !endDate && equipeConfirmado && !periodoSelecionado ? 700 : 400,
                color: !endDate && equipeConfirmado && !periodoSelecionado ? '#1976d2' : 'inherit',
                fontSize: !endDate && equipeConfirmado && !periodoSelecionado ? '1.1rem' : '1rem',
                textShadow: !endDate && equipeConfirmado && !periodoSelecionado ? '0 0 10px rgba(25, 118, 210, 0.3)' : 'none',
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: !endDate && equipeConfirmado && !periodoSelecionado ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                transition: 'all 0.3s ease-in-out',
                height: '56px',
                ...(!endDate && equipeConfirmado && !periodoSelecionado && {
                  '& fieldset': {
                    borderColor: '#1976d2 !important',
                    borderWidth: '2px',
                  },
                }),
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DatePicker
                label={!endDate && equipeConfirmado && !periodoSelecionado ? "DATA FIM *" : "Data Fim"}
                value={endDate}
                onChange={handleEndDateChange}
                disabled={!!periodoSelecionado}
                minDate={startDate || null}
                maxDate={
                  maxDateRange && startDate 
                    ? (() => {
                        const maxDate = new Date(startDate);
                        maxDate.setDate(maxDate.getDate() + maxDateRange);
                        return maxDate;
                      })()
                    : null
                }
                renderInput={(params) => {
                  let helperText = params.helperText;
                  let error = params.error;
                  
                  if (maxDateRange && startDate && endDate) {
                    const diffTime = Math.abs(endDate - startDate);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    if (diffDays > maxDateRange) {
                      error = true;
                      helperText = `Intervalo máximo de ${maxDateRange} dias excedido`;
                    } else {
                      helperText = `${diffDays} dia${diffDays !== 1 ? 's' : ''} selecionado${diffDays !== 1 ? 's' : ''}`;
                    }
                  }
                  
                  return (
                    <TextField 
                      {...params} 
                      fullWidth 
                      disabled={!!periodoSelecionado}
                      helperText={helperText}
                      error={error}
                    />
                  );
                }}
              />
            </LocalizationProvider>
            <Box sx={{ minHeight: '20px', mt: 0.5 }} />
          </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end', 
          flexShrink: 0, 
          flex: '0 0 auto',
          alignSelf: 'flex-start',
          height: '56px'
        }}>
          <Button
            variant="contained"
            color="primary"
            sx={{ 
              minWidth: 120, 
              whiteSpace: 'nowrap',
              height: '56px',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 4,
              }
            }}
            onClick={onCarregar}
            startIcon={<SyncIcon />}
            disabled={
              contratosLoading || 
              centrosLoading || 
              equipesLoading || 
              (limitContratoToOne 
                ? (Array.isArray(contrato) ? contrato.length === 0 : !contrato)
                : contrato.length === 0
              ) || 
              centro.length === 0 || 
              equipe.length === 0 ||
              !startDate ||
              !endDate
            }
          >
            Carregar
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default FiltersBar; 